import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { IndexTemplateComponent } from '@bisual/ngx-index-template';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ExamInstanceCreateDialogComponent } from 'src/app/components/main/employees/rrhh/exam-instance-create-dialog/exam-instance-create-dialog.component';
import { ArrayResponse } from 'src/app/models/array_response.interface';
import { ExamInstance } from 'src/app/models/exam_instance.model';
import { ApiService, UtilsService } from 'src/app/services';

type ExamInstanceRow = ExamInstance & {
  template_name_label: string;
  questions_count_label: string;
  total_minutes_label: string;
  evaluations_last_12_months_label: string;
  can_delete: boolean;
  delete_disabled_tooltip: string;
};

@Component({
  selector: 'app-exam-instances-list',
  templateUrl: './exam-instances-list.component.html',
  styleUrl: './exam-instances-list.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DatePipe,
    FormsModule,
    ReactiveFormsModule,
    RouterLink,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatPaginatorModule,
    MatTableModule,
    MatTooltipModule,
  ],
})
export class ExamInstancesListComponent extends IndexTemplateComponent implements OnInit {
  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  private readonly api_service = inject(ApiService);
  private readonly change_detector_ref = inject(ChangeDetectorRef);
  private readonly mat_dialog = inject(MatDialog);
  private readonly destroy_ref = inject(DestroyRef);
  private readonly snack_bar = inject(MatSnackBar);

  dataSource = new MatTableDataSource<ExamInstanceRow>([]);

  displayedColumns: string[] = [
    'name',
    'template',
    'questions_count',
    'total_minutes',
    'evaluations_last_12_months',
    'created_at',
    'actions',
  ];

  override filterFormExtraParams: Record<string, unknown> = {
    search: [''],
  };

  constructor() {
    super(inject(Router), inject(UntypedFormBuilder), inject(ActivatedRoute), inject(UtilsService));
  }

  override ngOnInit(): void {
    this.processUrlParams();

    const url_page = this.activatedRoute.snapshot.queryParamMap.get('page');
    if (!url_page) {
      this.pageIndex = 0;
    }

    super.ngOnInit();
    this.listenSearchFormControl();
  }

  openCreateDialog(): void {
    const dialog_ref = this.mat_dialog.open(ExamInstanceCreateDialogComponent, {
      width: '520px',
    });

    dialog_ref
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe((should_refresh: boolean | undefined): void => {
        if (should_refresh) {
          this.fetchData();
        }
      });
  }

  deleteExamInstance(exam_instance: ExamInstanceRow): void {
    if (!exam_instance.can_delete) {
      return;
    }

    if (!confirm(`¿Eliminar el examen "${exam_instance.name}"?`)) {
      return;
    }

    this.api_service
      .deleteExamInstance(exam_instance.id)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (): void => {
          if (this.dataSource.data.length === 1 && this.pageIndex > 0) {
            this.pageIndex -= 1;
          }
          this.fetchData();
          this.snack_bar.open('Examen eliminado', undefined, { duration: 3000 });
        },
        error: (err: { error?: { error?: string } }): void => {
          const message = err?.error?.error ?? 'Error al eliminar.';
          this.snack_bar.open(message, undefined, { duration: 5000 });
        },
      });
  }

  override fetchData(): void {
    const search_term = this.filterForm.get('search')?.value || '';

    const params: Record<string, unknown> = {
      page: this.pageIndex + 1,
      per_page: this.pageSize,
      order_by: 'id:desc',
      with: 'exam_template',
    };

    if (search_term) {
      params.search = search_term;
    }

    this.api_service
      .getExamInstancesPaged(params)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (response: ArrayResponse<ExamInstance>): void => {
          this.dataSource.data = response.data.map(exam_instance => this.mapExamInstanceRow(exam_instance));
          this.setMetadata(response.meta.total, response.meta.current_page - 1, response.meta.per_page);
          this.change_detector_ref.markForCheck();
        },
      });
  }

  override changePage(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.fetchData();
    super.changePage(event);
  }

  private mapExamInstanceRow(exam_instance: ExamInstance): ExamInstanceRow {
    const questions_count = exam_instance.exam_instance_items_count ?? exam_instance.exam_instance_items?.length ?? 0;
    const evaluations_count = exam_instance.exam_instance_evaluations_count ?? 0;
    const evaluations_last_12_months = exam_instance.exam_instance_evaluations_last_12_months_count ?? 0;
    const total_minutes = exam_instance.total_approximate_minutes ?? 0;
    const can_delete = evaluations_count === 0;

    return {
      ...exam_instance,
      template_name_label: exam_instance.exam_template?.name ?? '—',
      questions_count_label: String(questions_count),
      total_minutes_label: total_minutes > 0 ? `${total_minutes} min` : '—',
      evaluations_last_12_months_label: String(evaluations_last_12_months),
      can_delete,
      delete_disabled_tooltip: can_delete ? 'Eliminar' : 'No se puede eliminar: el examen tiene evaluaciones registradas',
    };
  }

  private listenSearchFormControl(): void {
    this.filterForm
      .get('search')
      ?.valueChanges.pipe(debounceTime(500), distinctUntilChanged(), takeUntilDestroyed(this.destroy_ref))
      .subscribe((): void => {
        this.pageIndex = 0;
        this.fetchData();
      });
  }

  private processUrlParams(): void {
    const url_per_page = this.activatedRoute.snapshot.queryParamMap.get('per_page');
    if (url_per_page) {
      this.pageSize = parseInt(url_per_page, 10);
    }

    const url_page = this.activatedRoute.snapshot.queryParamMap.get('page');
    if (url_page) {
      this.pageIndex = parseInt(url_page, 10) - 1;
    }
  }
}
